import React, { useEffect, useMemo, useState } from 'react';
import { Modal as AntModal, Button as AntButton, message, Tabs as AntTabs } from 'antd';
import { getCompanySmtpSettings, sendEmail } from '../services/smtpService';
import { getUserCompanyInfo } from '../services/apiService';
import { getAuthHeader, getUserInfo } from '../utils/tokenManager';

export default function DetailLinkEmailModal({ open, onClose, recipientEmail, productName, linkUrl }) {
  const [smtpSettings, setSmtpSettings] = useState(null);
  const [company, setCompany] = useState(null);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientError, setClientError] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientPicker, setShowClientPicker] = useState(false);

  const getDefaultEmailBody = (url, companyName, title) => {
    return `<!DOCTYPE html>
<html>
  <body style="font-family: Pretendard, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Malgun Gothic', Arial, sans-serif; color:#111827; line-height:1.7; font-size:14px; margin:0; padding:0;">
    <div style="max-width:640px; margin:0 auto; padding:16px;">
      <p style="margin:0 0 12px 0;">안녕하세요.</p>
      <p style="margin:0 0 20px 0;">${companyName || '저희 여행사'}입니다.<br/>상품 상세 페이지 링크를 보내드립니다.</p>

      <div style="background:#f9fafb; border:1px solid #e5e7eb; border-radius:8px; padding:12px; margin:0 0 16px 0;">
        <div style="margin:0 0 6px 0;"><strong style="color:#374151;">■ 상품명:</strong> ${title || '-'}</div>
      </div>

      <p style="margin:0 0 12px 0;">아래 버튼을 클릭하여 상품 상세 페이지를 확인하실 수 있습니다.</p>
      <p style="margin:0 0 20px 0;">
        <a href="${url || '#'}" target="_blank" style="display:inline-block; background:#2563eb; color:#ffffff; text-decoration:none; padding:10px 16px; border-radius:8px; font-weight:600;">상세 페이지 열기</a>
      </p>

      ${url ? `<p style="margin:0 0 20px 0; color:#6b7280; font-size:12px;">버튼이 보이지 않으면 아래 링크를 복사하여 브라우저에 붙여넣기 해주세요.<br/><span style="word-break:break-all;">${url}</span></p>` : ''}

      <p style="margin:0 0 12px 0;">문의사항이 있으시면 언제든지 연락 주시기 바랍니다.</p>
      <p style="margin:0 0 24px 0;">감사합니다.</p>
      <p style="margin:0;">${companyName || ''}</p>
    </div>
  </body>
</html>`;
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const smtp = await getCompanySmtpSettings();
        // SMTP 설정이 유효한지 확인 - 서버주소, 메일주소, 비밀번호가 모두 있어야 유효한 설정
        const isValidSmtp = smtp && smtp?.서버주소 && smtp?.메일주소 && smtp?.비밀번호;
        setSmtpSettings(isValidSmtp ? smtp : null);
      } catch (e) {
        console.warn('SMTP 설정 조회 실패:', e);
        setSmtpSettings(null);
      }
      try {
        const info = await getUserCompanyInfo();
        setCompany(info || null);
      } catch (e) {
        console.warn('회사 정보 조회 실패:', e);
      }
      try {
        setLoadingClients(true);
        setClientError('');
        const { companyNo } = getUserInfo() || {};
        const payload = {
          Table1: [
            { 컬럼명: '@업체번호', 값: String(companyNo ?? '') },
            { 컬럼명: '@사용여부1', 값: true },
            { 컬럼명: '@사용여부2', 값: true },
          ],
        };
        const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.CW_S_%ED%8C%90%EB%A7%A4%EA%B1%B0%EB%9E%98%EC%B2%98';
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error('거래처 목록을 불러오지 못했습니다.');
        const data = await res.json();
        setClients(Array.isArray(data?.table) ? data.table : []);
      } catch (e) {
        console.warn('거래처 목록 조회 실패:', e);
        setClientError('거래처 목록을 불러오지 못했습니다.');
      } finally {
        setLoadingClients(false);
      }
    })();
  }, [open]);

  const effectiveLinkUrl = useMemo(() => {
    if (!linkUrl) return '';
    const agentId = selectedClient?.업체번호2;
    if (!agentId) return linkUrl;
    const sep = linkUrl.includes('?') ? '&' : '?';
    return `${linkUrl}${sep}AgentID=${encodeURIComponent(String(agentId))}`;
  }, [linkUrl, selectedClient?.업체번호2]);

  useEffect(() => {
    if (!open) return;
    const defaultRecipient = String(recipientEmail || '').trim();
    const title = productName || '여행상품';
    if (!selectedClient) {
      setEmailRecipient(defaultRecipient);
    }
    setEmailSubject(`[상품상세 링크] ${title}`);
    setEmailBody(getDefaultEmailBody(effectiveLinkUrl, company?.업체명, title));
  }, [open, recipientEmail, productName, effectiveLinkUrl, company?.업체명, selectedClient]);

  const filteredClients = useMemo(() => {
    const q = (clientSearch || '').toLowerCase().trim();
    if (!q) return clients;
    return clients.filter(c => {
      const name = String(c?.거래처명 || '').toLowerCase();
      const email = String(c?.대표이메일 || '').toLowerCase();
      const owner = String(c?.대표자명 || '').toLowerCase();
      return name.includes(q) || email.includes(q) || owner.includes(q);
    });
  }, [clients, clientSearch]);

  const handleSend = async () => {
    if (!emailRecipient?.trim()) { message.error('수신자 이메일을 입력해주세요.'); return; }
    if (!emailSubject?.trim()) { message.error('메일 제목을 입력해주세요.'); return; }
    if (!smtpSettings) { message.error('SMTP 설정이 없습니다.'); return; }
    try {
      setSendingEmail(true);
      const hide = message.loading('이메일 발송 중...', 0);
      const recipients = emailRecipient.split(/[,;]/).map(e => e.trim()).filter(Boolean);
      await sendEmail({
        recipients,
        subject: emailSubject,
        body: emailBody,
        smtpServer: smtpSettings?.서버주소 || '',
        smtpPort: smtpSettings?.포트 || 465,
        smtpUsername: smtpSettings?.메일주소 || '',
        smtpPassword: smtpSettings?.비밀번호 || '',
      });
      hide();
      message.success('이메일이 성공적으로 발송되었습니다.');
      onClose && onClose();
    } catch (e) {
      console.error('상세 페이지 링크 이메일 발송 실패:', e);
      message.error(e?.message || '이메일 발송에 실패했습니다.');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <AntModal
      open={open}
      onCancel={onClose}
      footer={null}
      title={
        <div className="flex items-center">
          <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          상품 상세 페이지 링크 이메일 발송
        </div>
      }
      width={640}
      destroyOnClose
      maskClosable={false}
    >
      <div className="space-y-4 pt-2">
        <div className={`p-3 rounded-lg text-sm ${smtpSettings ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
          {smtpSettings ? (
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              SMTP 설정 완료 ({smtpSettings?.서버주소})
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                SMTP 설정이 필요합니다. 설정 {" > "} 발신메일 관리에서 설정해주세요.
              </div>
              <button
                type="button"
                className="text-xs px-2 py-1 rounded border border-amber-300 bg-amber-100 hover:bg-amber-200 text-amber-700 font-medium"
                onClick={() => {
                  // 발신메일 관리 페이지로 이동
                  window.location.href = '/settings/outgoing-mail';
                }}
              >
                발신메일 관리
              </button>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <label className="block text-sm font-medium text-gray-700">
              수신자 이메일 <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              className="inline-flex items-center text-xs px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-50"
              onClick={() => setShowClientPicker((v) => !v)}
            >
              거래처 선택
            </button>
          </div>
          <input
            className="w-full border border-gray-200 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
            value={emailRecipient}
            onChange={(e) => setEmailRecipient(e.target.value)}
            placeholder="example@email.com (여러 개는 쉼표/세미콜론 구분)"
          />
          {showClientPicker && (
            <div className="mt-2 border border-gray-200 rounded-lg bg-white">
              <div className="flex items-center px-3 py-2 border-b border-gray-100 gap-2">
                <input
                  className="flex-1 border border-gray-200 rounded px-2 py-1 text-xs"
                  placeholder="거래처명 / 대표자 / 이메일 검색"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                />
                {selectedClient && (
                  <button
                    type="button"
                    className="text-xs px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-50 whitespace-nowrap"
                    onClick={() => { setSelectedClient(null); }}
                  >
                    선택 해제
                  </button>
                )}
              </div>
              <div className="max-h-40 overflow-auto text-xs">
                {loadingClients && (
                  <div className="px-3 py-2 text-gray-500">거래처를 불러오는 중...</div>
                )}
                {!loadingClients && clientError && (
                  <div className="px-3 py-2 text-red-600">{clientError}</div>
                )}
                {!loadingClients && !clientError && filteredClients.length === 0 && (
                  <div className="px-3 py-2 text-gray-500">표시할 거래처가 없습니다.</div>
                )}
                {!loadingClients && !clientError && filteredClients.length > 0 && (
                  <ul className="divide-y divide-gray-100">
                    {filteredClients.slice(0, 50).map((c, idx) => (
                      <li
                        key={idx}
                        className={`px-3 py-1.5 cursor-pointer hover:bg-blue-50 ${selectedClient?.거래처번호 === c?.거래처번호 ? 'bg-blue-50' : ''}`}
                        onClick={() => {
                          setSelectedClient(c);
                          const repEmail = String(c?.대표이메일 || '').trim();
                          if (repEmail) setEmailRecipient(repEmail);
                        }}
                      >
                        <div className="flex justify-between items-center gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-800 truncate">{c?.거래처명 || '-'}</div>
                            <div className="text-[11px] text-gray-500 truncate">{c?.대표자명 || ''}</div>
                          </div>
                          <div className="flex-1 min-w-0 text-right">
                            <div className="text-[11px] text-gray-600 truncate">{c?.대표이메일 || ''}</div>
                            {c?.업체번호2 && (
                              <div className="text-[10px] text-gray-400">AgentID: {c.업체번호2}</div>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">메일 제목 <span className="text-red-500">*</span></label>
          <input
            className="w-full border border-gray-200 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder="메일 제목을 입력하세요"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">메일 본문</label>
          <AntTabs
            defaultActiveKey="preview"
            items={[
              {
                key: 'edit',
                label: '편집',
                children: (
                  <textarea
                    className="w-full border border-gray-200 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm h-48 font-mono"
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    placeholder="메일 본문을 입력하세요 (HTML 태그 사용 가능)"
                  />
                ),
              },
              {
                key: 'preview',
                label: '미리보기',
                children: (
                  <div className="border border-gray-200 rounded-lg bg-white p-4 max-h-96 overflow-auto" dangerouslySetInnerHTML={{ __html: emailBody || '' }} />
                ),
              },
            ]}
          />
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
          <AntButton onClick={onClose} size="large">취소</AntButton>
          <AntButton type="primary" onClick={handleSend} loading={sendingEmail} disabled={!smtpSettings} size="large">발송</AntButton>
        </div>
      </div>
    </AntModal>
  );
}
